import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type {
  BlockEditorComponent,
  BlockValues,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { DefaultEditor } from "./DefaultEditor";
import { useBlockFormWatch } from "./hooks/useBlockFormWatch";
import { blockToValues } from "./utils/blockToValues";

export interface EditorComponents {
  [key: string]: BlockEditorComponent;
}

export interface EditorFormProps {
  selectedBlock: BaseBlock;
}

export const EditorForm = ({ selectedBlock }: EditorFormProps) => {
  const components = useBuilderStore(state => state.getBlockEditors());
  const EditorComponent = components[selectedBlock.type] || DefaultEditor;
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);
  const appearance = useBuilderStore(state => state.appearance);
  const values = useMemo(
    () => blockToValues(selectedBlock, currentCanvasTheme, appearance),
    [selectedBlock, currentCanvasTheme, appearance],
  );

  const methods = useForm<BlockValues>({
    defaultValues: values,
    mode: "onChange",
  });

  const { reset, watch } = methods;

  useEffect(() => {
    reset(values);
  }, [reset, values, selectedBlock.props]);

  useBlockFormWatch({ watch });

  return (
    <FormProvider {...methods}>
      <EditorComponent block={selectedBlock} values={values} />
    </FormProvider>
  );
};
